// src/pages/Resources/CheckPermissionsModal.tsx
import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { FaSearch, FaUserCheck } from 'react-icons/fa';

interface PermissionResult {
  hasAccess: boolean;
  permissionLevel?: string;
  grantedThrough?: string;
  groupName?: string;
  details?: string;
}

interface Props {
  show: boolean;
  groupName: string;
  internalResourceId: string;
  objectName?: string; // For list/library/item names
  objectId?: string;    // For specific item permissions
  onClose: () => void;
}

const CheckPermissionsModal: React.FC<Props> = ({
  show,
  groupName,
  internalResourceId,
  objectName,
  objectId,
  onClose
}) => {
  const [emailOrName, setEmailOrName] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<PermissionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCheck = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailOrName.trim()) return;

    setChecking(true);
    setError(null);
    setResult(null);

    try {
      const params = new URLSearchParams({
        userEmailOrName: emailOrName.trim(),
      });

      if (objectId) {
        params.append('objectId', objectId);
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/resources/${internalResourceId}/check-permissions?${params}`,
        {
          headers: {
            Authorization: `Bearer ${await acquireToken()}`,
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to check permissions');
      }

      const data: PermissionResult = await response.json();
      setResult(data);
    } catch (err: any) {
      console.error('Error checking permissions:', err);
      setError(err.message || 'Failed to check permissions. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const acquireToken = async () => {
    // Placeholder - should come from context/parent
    return 'token';
  };

  const handleClose = () => {
    setEmailOrName('');
    setResult(null);
    setError(null);
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaUserCheck className="me-2" />
          Check User Permissions
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleCheck}>
        <Modal.Body>
          <div className="alert alert-info small mb-3">
            <strong>Check Permissions</strong>
            <p className="mb-0 mt-1">
              Enter a user's email address or name to check their permissions on{' '}
              {objectName ? (
                <>
                  <strong>{objectName}</strong> in <strong>{groupName}</strong>
                </>
              ) : (
                <strong>{groupName}</strong>
              )}
            </p>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              Email Address or Display Name <span className="text-danger">*</span>
            </Form.Label>
            <div className="input-group">
              <span className="input-group-text">
                <FaSearch />
              </span>
              <Form.Control
                type="text"
                value={emailOrName}
                onChange={(e) => setEmailOrName(e.target.value)}
                placeholder="e.g., john.doe@company.com or John Doe"
                required
                disabled={checking}
                autoFocus
              />
            </div>
            <Form.Text className="text-muted">
              Enter the user's email address or full display name
            </Form.Text>
          </Form.Group>

          {/* Error */}
          {error && (
            <Alert variant="danger">
              <strong>Error:</strong> {error}
            </Alert>
          )}

          {/* Checking Spinner */}
          {checking && (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <div className="mt-2 text-muted">Checking permissions...</div>
            </div>
          )}

          {/* Results */}
          {result && !checking && (
            <div className="border rounded p-3 bg-light">
              <h6 className="mb-3">Permission Results:</h6>
              
              {result.hasAccess ? (
                <div>
                  <Alert variant="success" className="mb-3">
                    <strong>✓ User has access</strong>
                  </Alert>
                  
                  {result.permissionLevel && (
                    <div className="mb-2">
                      <span className="fw-semibold">Permission Level:</span>{' '}
                      <span className="badge bg-primary ms-2">{result.permissionLevel}</span>
                    </div>
                  )}
                  
                  {result.grantedThrough && (
                    <div className="mb-2">
                      <span className="fw-semibold">Granted Through:</span>{' '}
                      <span className="ms-2">{result.grantedThrough}</span>
                    </div>
                  )}
                  
                  {result.groupName && (
                    <div className="mb-2">
                      <span className="fw-semibold">Via Group:</span>{' '}
                      <span className="ms-2">{result.groupName}</span>
                    </div>
                  )}
                  
                  {result.details && (
                    <div className="mt-3 small text-muted">
                      {result.details}
                    </div>
                  )}
                </div>
              ) : (
                <Alert variant="warning">
                  <strong>ⓘ User does not have access</strong>
                  {result.details && (
                    <div className="mt-2 small">{result.details}</div>
                  )}
                </Alert>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={checking}>
            Close
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            disabled={!emailOrName.trim() || checking}
          >
            {checking ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Checking...
              </>
            ) : (
              <>
                <FaUserCheck className="me-2" />
                Check Permissions
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CheckPermissionsModal;
